/**
 * User management: list users, filter by type, edit user type/role, suspend/unsuspend, delete.
 * Admin-only: uses useAdmin() and redirects non-admins; portal nav shows link only for admins.
 */
import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useAdmin } from '@/hooks/useAdmin'
import { Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'

const ADMIN_USERS_QUERY_KEY = ['admin-users'] as const

const USER_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'broadcaster', label: 'Broadcaster' },
  { value: 'ad-creator', label: 'Ad-Creator' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'admin', label: 'Admin' },
]

interface ProfileRow {
  id: string
  display_name: string | null
  email: string | null
  user_type: string | null
  role: string | null
  is_suspended: boolean
  created_at: string
  last_seen_at?: string | null
  thread_count?: number
  comment_count?: number
  deletion_requested_at?: string | null
  status?: string | null
  deleted_at?: string | null
  must_change_password?: boolean
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

export default function AdminUsersPage() {
  const { user, session } = useAuth()
  const isAdmin = useAdmin()
  const queryClient = useQueryClient()
  const [filterUserType, setFilterUserType] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [pendingDeleteUser, setPendingDeleteUser] = useState<ProfileRow | null>(null)

  const { data: allProfiles = [], isLoading, error } = useQuery({
    queryKey: ADMIN_USERS_QUERY_KEY,
    queryFn: async (): Promise<ProfileRow[]> => {
      const { data, error: err } = await supabase.rpc('get_admin_user_list')
      if (!err) {
        return (data ?? []).map((row: Record<string, unknown>) => ({
          ...row,
          email: row.email ?? null,
          is_suspended: row.is_suspended === true,
          last_seen_at: row.last_seen_at ?? null,
          thread_count: row.thread_count != null ? Number(row.thread_count) : 0,
          comment_count: row.comment_count != null ? Number(row.comment_count) : 0,
          deletion_requested_at: row.deletion_requested_at ?? null,
          status: row.status ?? null,
          deleted_at: row.deleted_at ?? null,
          must_change_password: row.must_change_password === true,
        })) as ProfileRow[]
      }
      // Fallback if migration 033 not applied: fetch profiles without email
      if (err.code === '42883' || err.message?.includes('function') || err.message?.includes('does not exist')) {
        const { data: fallback, error: fallbackErr } = await supabase
          .from('profiles')
          .select('id, display_name, user_type, role, is_suspended, created_at')
          .order('created_at', { ascending: false })
        if (fallbackErr) throw fallbackErr
        return (fallback ?? []).map((row: Record<string, unknown>) => ({
          ...row,
          email: null,
          is_suspended: row.is_suspended === true,
          last_seen_at: null,
          thread_count: 0,
          comment_count: 0,
          deletion_requested_at: null,
          status: null,
          deleted_at: null,
          must_change_password: false,
        })) as ProfileRow[]
      }
      throw err
    },
    enabled: !!user && isAdmin,
  })

  const profilesByType =
    filterUserType === 'all'
      ? allProfiles
      : allProfiles.filter((p) => (p.user_type || p.role || 'normal') === filterUserType)

  const profiles =
    filterStatus === 'all'
      ? profilesByType
      : profilesByType.filter((p) => (p.status || 'active') === filterStatus)

  const updateUserTypeMutation = useMutation({
    mutationFn: async ({ profileId, userType }: { profileId: string; userType: string }) => {
      const { error } = await supabase
        .from('profiles')
        .update({ user_type: userType, role: userType })
        .eq('id', profileId)
      if (error) throw error
    },
    onSuccess: (_, { profileId, userType }) => {
      if (user?.id) {
        supabase
          .from('admin_audit_log')
          .insert({
            actor_id: user.id,
            action_type: 'user_type_change',
            target_type: 'user',
            target_id: profileId,
            details: { new_user_type: userType },
          })
          .then(() => queryClient.invalidateQueries({ queryKey: ['admin-audit-entries'] }))
      }
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['profile', profileId] })
      queryClient.invalidateQueries({ queryKey: ['profile-suspended-check', profileId] })
      setMessage({ type: 'success', text: 'User type updated.' })
      setTimeout(() => setMessage(null), 3000)
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message || 'Failed to update user type.' })
    },
  })

  const setSuspendedMutation = useMutation({
    mutationFn: async ({ profileId, suspended }: { profileId: string; suspended: boolean }) => {
      const { error } = await supabase
        .from('profiles')
        .update({ is_suspended: suspended })
        .eq('id', profileId)
      if (error) throw error
    },
    onSuccess: (_, { profileId, suspended }) => {
      if (user?.id) {
        supabase
          .from('admin_audit_log')
          .insert({
            actor_id: user.id,
            action_type: suspended ? 'suspend' : 'unsuspend',
            target_type: 'user',
            target_id: profileId,
            details: {},
          })
          .then(() => queryClient.invalidateQueries({ queryKey: ['admin-audit-entries'] }))
      }
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['profile', profileId] })
      queryClient.invalidateQueries({ queryKey: ['profile-suspended-check', profileId] })
      setMessage({
        type: 'success',
        text: 'Suspension status updated. The user will see the suspended screen on next load.',
      })
      setTimeout(() => setMessage(null), 4000)
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message || 'Failed to update suspension status.' })
    },
  })

  const handleSuspendClick = (p: ProfileRow) => {
    if (p.is_suspended) {
      setSuspendedMutation.mutate({ profileId: p.id, suspended: false })
    } else {
      const confirmed = window.confirm(
        'Suspend this user? They will not be able to use the platform until you unsuspend them.'
      )
      if (confirmed) setSuspendedMutation.mutate({ profileId: p.id, suspended: true })
    }
  }

  type DeleteUserPayload = { targetUserId: string; anonymise?: boolean }

  const deleteUserMutation = useMutation({
    mutationFn: async (payload: DeleteUserPayload) => {
      if (!user?.id || !session?.access_token) throw new Error('Not authenticated')
      const { targetUserId, anonymise = false } = payload
      const { error } = await supabase.rpc('delete_user_reassign', {
        to_delete_user_id: targetUserId,
        reassign_to_admin_id: user.id,
        anonymise_instead_of_reassign: anonymise,
      })
      if (error) throw error
      const url = `${SUPABASE_URL}/functions/v1/delete-user`
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ userId: targetUserId }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data?.error || `Delete failed (${res.status})`)
    },
    onSuccess: (_, payload) => {
      const { targetUserId, anonymise = false } = payload
      if (user?.id) {
        supabase
          .from('admin_audit_log')
          .insert({
            actor_id: user.id,
            action_type: 'delete_user',
            target_type: 'user',
            target_id: targetUserId,
            details: anonymise ? { anonymise: true } : {},
          })
          .then(() => queryClient.invalidateQueries({ queryKey: ['admin-audit-entries'] }))
      }
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY })
      setMessage({
        type: 'success',
        text: anonymise
          ? 'User permanently deleted. Their content is now shown as Deleted User.'
          : 'User permanently deleted. Their content was reassigned to you.',
      })
      setTimeout(() => setMessage(null), 5000)
      setPendingDeleteUser(null)
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message || 'Failed to delete user.' })
    },
  })

  const handleDeleteClick = (p: ProfileRow) => {
    setPendingDeleteUser(p)
  }

  const handlePurgeConfirm = (anonymise: boolean) => {
    if (!pendingDeleteUser) return
    deleteUserMutation.mutate({ targetUserId: pendingDeleteUser.id, anonymise })
  }

  const rejectDeletionRequestMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const { error } = await supabase.rpc('reject_deletion_request', { target_user_id: targetUserId })
      if (error) throw error
    },
    onSuccess: (_, targetUserId) => {
      if (user?.id) {
        supabase
          .from('admin_audit_log')
          .insert({
            actor_id: user.id,
            action_type: 'reject_deletion_request',
            target_type: 'user',
            target_id: targetUserId,
            details: {},
          })
          .then(() => queryClient.invalidateQueries({ queryKey: ['admin-audit-entries'] }))
      }
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY })
      setMessage({ type: 'success', text: 'Deletion request rejected. User can continue using their account.' })
      setTimeout(() => setMessage(null), 5000)
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message || 'Failed to reject deletion request.' })
    },
  })

  const softDeleteUserMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const { error } = await supabase.rpc('soft_delete_user', { target_user_id: targetUserId })
      if (error) throw error
    },
    onSuccess: (_, targetUserId) => {
      if (user?.id) {
        supabase
          .from('admin_audit_log')
          .insert({
            actor_id: user.id,
            action_type: 'soft_delete_user',
            target_type: 'user',
            target_id: targetUserId,
            details: {},
          })
          .then(() => queryClient.invalidateQueries({ queryKey: ['admin-audit-entries'] }))
      }
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY })
      setMessage({ type: 'success', text: 'User soft-deleted. They can be restored or purged from this list.' })
      setTimeout(() => setMessage(null), 5000)
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message || 'Failed to soft-delete user.' })
    },
  })

  const restoreSoftDeletedMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const { error } = await supabase.rpc('restore_soft_deleted_user', { target_user_id: targetUserId })
      if (error) throw error
    },
    onSuccess: (_, targetUserId) => {
      if (user?.id) {
        supabase
          .from('admin_audit_log')
          .insert({
            actor_id: user.id,
            action_type: 'restore_soft_deleted',
            target_type: 'user',
            target_id: targetUserId,
            details: {},
          })
          .then(() => queryClient.invalidateQueries({ queryKey: ['admin-audit-entries'] }))
      }
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY })
      setMessage({ type: 'success', text: 'User restored. They can sign in again.' })
      setTimeout(() => setMessage(null), 5000)
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message || 'Failed to restore user.' })
    },
  })

  const requirePasswordChangeMutation = useMutation({
    mutationFn: async (profileId: string) => {
      const { error } = await supabase
        .from('profiles')
        .update({ must_change_password: true })
        .eq('id', profileId)
      if (error) throw error
    },
    onSuccess: (_, profileId) => {
      if (user?.id) {
        supabase
          .from('admin_audit_log')
          .insert({
            actor_id: user.id,
            action_type: 'require_password_change',
            target_type: 'user',
            target_id: profileId,
            details: {},
          })
          .then(() => queryClient.invalidateQueries({ queryKey: ['admin-audit-entries'] }))
      }
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['profile-suspended-check', profileId] })
      setMessage({
        type: 'success',
        text: 'User will be asked to set a new password on their next login.',
      })
      setTimeout(() => setMessage(null), 4000)
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message || 'Failed to set require password change.' })
    },
  })

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />
  }

  const displayUserType = (p: ProfileRow) => p.user_type || p.role || 'normal'

  return (
    <div className="max-w-5xl mx-auto">
      <h1
        className="text-3xl font-bold mb-2"
        style={{ color: 'var(--text-primary)' }}
      >
        User management
      </h1>
      <p className="mb-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
        Change a user&apos;s type below. Only admins can access this page.
      </p>

      {message && (
        <div
          className="mb-4 px-4 py-2 rounded border-2"
          role="alert"
          style={{
            backgroundColor: message.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
            color: 'var(--text-inverse)',
            borderColor: message.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
          }}
        >
          {message.text}
        </div>
      )}

      {pendingDeleteUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="purge-modal-title"
          onClick={() => !deleteUserMutation.isPending && setPendingDeleteUser(null)}
        >
          <div
            className="rounded-lg border-2 p-6 max-w-md w-full"
            style={{
              backgroundColor: 'var(--bg-primary)',
              borderColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="purge-modal-title" className="text-xl font-bold mb-2">
              Permanently delete this user?
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              {pendingDeleteUser.display_name || pendingDeleteUser.email || pendingDeleteUser.id}. This cannot be undone.
            </p>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Choose how to handle their content (groups, threads, comments):
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                disabled={deleteUserMutation.isPending}
                className="px-4 py-2 rounded border-2 font-medium"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  backgroundColor: 'var(--bg-interactive)',
                  color: 'var(--text-inverse)',
                }}
                onClick={() => handlePurgeConfirm(false)}
              >
                Reassign content to me
              </button>
              <button
                type="button"
                disabled={deleteUserMutation.isPending}
                className="px-4 py-2 rounded border-2 font-medium"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                }}
                onClick={() => handlePurgeConfirm(true)}
              >
                Anonymise (show as Deleted User)
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded border-2"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                }}
                onClick={() => setPendingDeleteUser(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="rounded-lg p-6 border-2 mb-6"
        style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-secondary)' }}
      >
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <label htmlFor="filter-user-type" className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            Type:
          </label>
          <select
            id="filter-user-type"
            value={filterUserType}
            onChange={(e) => setFilterUserType(e.target.value)}
            className="text-xs px-2 py-1 rounded border"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="all">All</option>
            {USER_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <label htmlFor="filter-status" className="text-sm font-medium ml-2" style={{ color: 'var(--text-primary)' }}>
            Status:
          </label>
          <select
            id="filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-xs px-2 py-1 rounded border"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="deletion_requested">Deletion requested</option>
            <option value="soft_deleted">Soft deleted</option>
          </select>
        </div>

        {error && (
          <p className="text-sm mb-4" style={{ color: 'var(--color-error)' }} role="alert">
            Failed to load users. {error instanceof Error ? error.message : 'Unknown error.'}
          </p>
        )}

        {isLoading && (
          <p style={{ color: 'var(--text-secondary)' }}>Loading users…</p>
        )}

        {!isLoading && !error && profiles.length === 0 && (
          <p style={{ color: 'var(--text-secondary)' }}>No users match the filter.</p>
        )}

        {!isLoading && !error && profiles.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" aria-label="Users" aria-describedby="users-table-summary">
              <caption id="users-table-summary" className="sr-only">
                User list with display name, email, type, status, last active, thread and comment counts, joined date, and actions
              </caption>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Name</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Email</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Type</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Status</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Last active</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }} title="Threads">T</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }} title="Comments">C</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Joined</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((p) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-primary)' }}>
                      {p.display_name || '—'}
                    </td>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {p.email || '—'}
                    </td>
                    <td className="py-1 px-1.5">
                      <select
                        aria-label={`User type for ${p.display_name || p.id}`}
                        value={displayUserType(p)}
                        onChange={(e) => {
                          const newType = e.target.value
                          if (newType === displayUserType(p)) return
                          updateUserTypeMutation.mutate({ profileId: p.id, userType: newType })
                        }}
                        disabled={updateUserTypeMutation.isPending || p.id === user?.id}
                        title={p.id === user?.id ? 'You cannot change your own user type' : undefined}
                        className="text-xs px-1.5 py-0.5 rounded border"
                        style={{
                          borderColor: 'var(--bg-tertiary)',
                          backgroundColor: 'var(--bg-primary)',
                          color: 'var(--text-primary)',
                        }}
                      >
                        {USER_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1 px-1.5">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="text-xs" style={{ color: (p.status === 'soft_deleted' || p.status === 'deletion_requested' || p.deletion_requested_at || p.is_suspended) ? 'var(--color-error)' : 'var(--text-secondary)' }}>
                          {p.status === 'soft_deleted' ? 'Soft deleted' : p.status === 'deletion_requested' || p.deletion_requested_at ? 'Deletion requested' : p.is_suspended ? 'Suspended' : 'Active'}
                        </span>
                        {p.must_change_password && (p.status || 'active') === 'active' && (
                          <span className="text-xs px-1 py-0 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }} title="User must set a new password on next login">
                            Pwd reset
                          </span>
                        )}
                        {p.id !== user?.id && (p.status || 'active') === 'active' && !p.deletion_requested_at && (
                          <button
                            type="button"
                            onClick={() => handleSuspendClick(p)}
                            disabled={setSuspendedMutation.isPending}
                            className="px-1 py-0.5 text-xs rounded border shrink-0"
                            style={{
                              borderColor: 'var(--bg-tertiary)',
                              backgroundColor: p.is_suspended ? 'var(--color-success)' : 'var(--color-error)',
                              color: 'var(--text-inverse)',
                            }}
                            aria-label={p.is_suspended ? `Unsuspend ${p.display_name || p.id}` : `Suspend ${p.display_name || p.id}`}
                          >
                            {p.is_suspended ? 'Unsuspend' : 'Suspend'}
                          </button>
                        )}
                        {p.id !== user?.id && (p.status === 'deletion_requested' || p.deletion_requested_at) && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm('Soft-delete this user? They will be signed out and their display name anonymised. You can Restore or Purge later.')) {
                                  softDeleteUserMutation.mutate(p.id)
                                }
                              }}
                              disabled={softDeleteUserMutation.isPending}
                              className="px-1 py-0.5 text-xs rounded border shrink-0"
                              style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-interactive)', color: 'var(--text-inverse)' }}
                              aria-label={`Soft delete ${p.display_name || p.id}`}
                            >
                              {softDeleteUserMutation.isPending ? '…' : 'Soft del'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setPendingDeleteUser(p)}
                              disabled={deleteUserMutation.isPending}
                              className="px-1 py-0.5 text-xs rounded border shrink-0"
                              style={{ borderColor: 'var(--color-error)', backgroundColor: 'var(--color-error)', color: 'var(--text-inverse)' }}
                              aria-label={`Purge ${p.display_name || p.id}`}
                            >
                              {deleteUserMutation.isPending ? '…' : 'Purge'}
                            </button>
                            <button
                              type="button"
                              onClick={() => rejectDeletionRequestMutation.mutate(p.id)}
                              disabled={rejectDeletionRequestMutation.isPending}
                              className="px-1 py-0.5 text-xs rounded border shrink-0"
                              style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                              aria-label={`Reject deletion request for ${p.display_name || p.id}`}
                            >
                              {rejectDeletionRequestMutation.isPending ? '…' : 'Reject'}
                            </button>
                          </>
                        )}
                        {p.id !== user?.id && p.status === 'soft_deleted' && (
                          <>
                            <button
                              type="button"
                              onClick={() => restoreSoftDeletedMutation.mutate(p.id)}
                              disabled={restoreSoftDeletedMutation.isPending}
                              className="px-1 py-0.5 text-xs rounded border shrink-0"
                              style={{ borderColor: 'var(--color-success)', backgroundColor: 'var(--color-success)', color: 'var(--text-inverse)' }}
                              aria-label={`Restore ${p.display_name || p.id}`}
                            >
                              {restoreSoftDeletedMutation.isPending ? '…' : 'Restore'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setPendingDeleteUser(p)}
                              disabled={deleteUserMutation.isPending}
                              className="px-1 py-0.5 text-xs rounded border shrink-0"
                              style={{ borderColor: 'var(--color-error)', backgroundColor: 'var(--color-error)', color: 'var(--text-inverse)' }}
                              aria-label={`Purge ${p.display_name || p.id}`}
                            >
                              {deleteUserMutation.isPending ? '…' : 'Purge'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {p.last_seen_at ? format(new Date(p.last_seen_at), 'dd/MM/yyyy, HH:mm') : 'Never'}
                    </td>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {(p.thread_count ?? 0).toLocaleString()}
                    </td>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {(p.comment_count ?? 0).toLocaleString()}
                    </td>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {format(new Date(p.created_at), 'dd/MM/yyyy')}
                    </td>
                    <td className="py-1 px-1.5">
                      <div className="flex flex-nowrap items-center gap-1">
                        {updateUserTypeMutation.isPending ? (
                          <span className="text-xs shrink-0" style={{ color: 'var(--text-secondary)' }}>Saving…</span>
                        ) : null}
                        {p.id !== user?.id && (p.status || 'active') === 'active' && !p.deletion_requested_at && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm('Require this user to set a new password on their next login? They will not be deleted.')) {
                                  requirePasswordChangeMutation.mutate(p.id)
                                }
                              }}
                              disabled={requirePasswordChangeMutation.isPending}
                              className="px-1 py-0.5 text-xs rounded border shrink-0 whitespace-nowrap"
                              style={{
                                borderColor: 'var(--bg-tertiary)',
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                              }}
                              aria-label={`Require password change for ${p.display_name || p.id}`}
                              title="Require password change on next login"
                            >
                              {requirePasswordChangeMutation.isPending ? '…' : 'Reset pwd'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(p)}
                              disabled={deleteUserMutation.isPending}
                              className="px-1 py-0.5 text-xs rounded border shrink-0"
                              style={{
                                borderColor: 'var(--color-error)',
                                backgroundColor: 'transparent',
                                color: 'var(--color-error)',
                              }}
                              aria-label={`Permanently delete ${p.display_name || p.id}`}
                              title="Permanently delete user"
                            >
                              {deleteUserMutation.isPending ? '…' : 'Delete'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
